import { BookMarked, Brain, Link2, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { SegmentResponse } from '@/api/type'
import type { QuestionFormat } from '@/api/types/flashcard'
import { Button } from '@/components/ui/button'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { SILENCE_TEXT } from '@/lib/constants'
import { useFlashcardStore } from '@/store/useFlashcardStore'

type FlashcardRegisterSheetProps = {
  isOpen: boolean
  onClose: () => void
  episodeId: number
  segmentIndex: number
  segments: SegmentResponse[]
}

type Mode = 'select' | 'ai_format' | 'ai_preview' | 'section_select'

const FORMAT_OPTIONS: { value: QuestionFormat; label: string; description: string }[] = [
  { value: 'auto', label: 'AI Auto', description: 'AIが最適な形式を選択' },
  { value: 'cloze_choice', label: 'Fill in (choices)', description: '穴埋め＋選択肢' },
  { value: 'cloze_free', label: 'Fill in (free)', description: '穴埋め自由回答' },
  { value: 'listening', label: 'Listening', description: '聴いて書く' },
  { value: 'definition', label: 'Definition', description: '英英定義→単語' },
  { value: 'paraphrase', label: 'Paraphrase', description: '言い換え' },
  { value: 'translation_ja_to_en', label: 'JA → EN', description: '日本語→英語' },
]

export const FlashcardRegisterSheet = ({
  isOpen,
  onClose,
  episodeId,
  segmentIndex,
  segments,
}: FlashcardRegisterSheetProps) => {
  const [mode, setMode] = useState<Mode>('select')
  const [selectedFormat, setSelectedFormat] = useState<QuestionFormat>('auto')
  const [generatedFront, setGeneratedFront] = useState('')
  const [generatedBack, setGeneratedBack] = useState('')
  const [generatedFormat, setGeneratedFormat] = useState<QuestionFormat>('auto')
  const [generatedChoices, setGeneratedChoices] = useState<string[] | null>(null)
  const [jaHint, setJaHint] = useState<string | null>(null)
  const { isSubmitting: isGenerating, run } = useSubmitLock()
  const [selectedBackSegmentIndex, setSelectedBackSegmentIndex] = useState<number | null>(null)

  const { addCard, generate } = useFlashcardStore()

  const segment = segments[segmentIndex]

  if (!isOpen || !segment) return null

  const handleGenerateAI = async () => {
    await run(async () => {
      try {
        const contextBefore = segments
          .slice(Math.max(0, segmentIndex - 2), segmentIndex)
          .filter((s) => s.text !== SILENCE_TEXT)
          .map((s) => s.text)
        const contextAfter = segments
          .slice(segmentIndex + 1, segmentIndex + 3)
          .filter((s) => s.text !== SILENCE_TEXT)
          .map((s) => s.text)

        const result = await generate({
          episode_id: episodeId,
          segment_index: segmentIndex,
          segment_text: segment.text,
          context_before: contextBefore,
          context_after: contextAfter,
          question_format: selectedFormat,
        })

        setGeneratedFront(result.front_text)
        setGeneratedBack(result.back_text)
        setGeneratedFormat(result.question_format)
        setGeneratedChoices(result.choices ?? null)
        setJaHint(result.ja_hint ?? null)
        setMode('ai_preview')
      } catch {
        toast.error('生成に失敗しました')
      }
    })
  }

  const handleRegisterAI = async () => {
    try {
      await addCard({
        episode_id: episodeId,
        segment_index: segmentIndex,
        card_type: 'ai_generated',
        front_text: generatedFront,
        back_text: generatedBack,
        front_segment_index: segmentIndex,
        question_format: generatedFormat,
        audio_start: segment.start,
        audio_end: segment.end,
      })
      toast.success('カードを登録しました')
      onClose()
    } catch {
      toast.error('登録に失敗しました')
    }
  }

  const handleRegisterSectionRef = async () => {
    if (selectedBackSegmentIndex === null) return
    const backSegment = segments[selectedBackSegmentIndex]

    try {
      await addCard({
        episode_id: episodeId,
        segment_index: segmentIndex,
        card_type: 'section_ref',
        front_text: segment.text,
        back_text: backSegment.text,
        front_segment_index: segmentIndex,
        back_segment_index: selectedBackSegmentIndex,
        question_format: 'cloze_free',
        audio_start: segment.start,
        audio_end: segment.end,
      })
      toast.success('カードを登録しました')
      onClose()
    } catch {
      toast.error('登録に失敗しました')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={onClose}>
      <div
        className="bg-background w-full rounded-t-2xl max-h-[80vh] overflow-y-auto p-6 animate-in slide-in-from-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="flex items-center gap-2 mb-4">
          <BookMarked className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">単語帳に登録</h2>
        </div>

        {/* 対象セグメント表示 */}
        <div className="p-3 bg-muted rounded-md mb-4 text-sm">
          <span className="text-xs text-muted-foreground mr-2">#{segmentIndex}</span>
          {segment.text}
        </div>

        {/* モード選択 */}
        {mode === 'select' && (
          <div className="space-y-3">
            <Button className="w-full justify-start gap-3 h-14" variant="outline" onClick={() => setMode('ai_format')}>
              <Brain className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">AIで問題生成</div>
                <div className="text-xs text-muted-foreground">文脈に合った問題を自動生成</div>
              </div>
            </Button>
            <Button
              className="w-full justify-start gap-3 h-14"
              variant="outline"
              onClick={() => setMode('section_select')}
            >
              <Link2 className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">セクション参照</div>
                <div className="text-xs text-muted-foreground">別のセグメントを回答に指定</div>
              </div>
            </Button>
          </div>
        )}

        {/* AI形式選択 */}
        {mode === 'ai_format' && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground">問題形式を選択</h3>
            <div className="grid grid-cols-2 gap-2">
              {FORMAT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedFormat(opt.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    selectedFormat === opt.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-xs text-muted-foreground">{opt.description}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="ghost" onClick={() => setMode('select')} className="flex-1">
                戻る
              </Button>
              <Button onClick={handleGenerateAI} disabled={isGenerating} className="flex-1">
                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                生成する
              </Button>
            </div>
          </div>
        )}

        {/* AIプレビュー */}
        {mode === 'ai_preview' && (
          <div className="space-y-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">問題 ({generatedFormat})</div>
              <div className="p-3 bg-muted rounded-md text-sm">{generatedFront}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">回答</div>
              <div className="p-3 bg-muted rounded-md text-sm">{generatedBack}</div>
            </div>
            {generatedChoices && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">選択肢</div>
                <div className="flex flex-wrap gap-2">
                  {generatedChoices.map((c, i) => (
                    <span key={i} className="px-2 py-1 bg-secondary rounded text-xs">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {jaHint && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">日本語ヒント</div>
                <div className="p-2 bg-yellow-500/10 rounded text-xs text-yellow-700 dark:text-yellow-300">
                  {jaHint}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setMode('ai_format')} className="flex-1">
                再生成
              </Button>
              <Button onClick={handleRegisterAI} className="flex-1">
                登録する
              </Button>
            </div>
          </div>
        )}

        {/* セクション参照モード */}
        {mode === 'section_select' && (
          <div className="space-y-3">
            <SectionSelectList
              segments={segments}
              segmentIndex={segmentIndex}
              selectedBackSegmentIndex={selectedBackSegmentIndex}
              onSelect={setSelectedBackSegmentIndex}
            />
            <div className="flex gap-2 mt-4">
              <Button variant="ghost" onClick={() => setMode('select')} className="flex-1">
                戻る
              </Button>
              <Button
                onClick={handleRegisterSectionRef}
                disabled={selectedBackSegmentIndex === null}
                className="flex-1"
              >
                登録する
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** セクション参照リスト - 選択セグメント付近に自動スクロール */
const SectionSelectList = ({
  segments,
  segmentIndex,
  selectedBackSegmentIndex,
  onSelect,
}: {
  segments: SegmentResponse[]
  segmentIndex: number
  selectedBackSegmentIndex: number | null
  onSelect: (i: number) => void
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // 選択セグメント付近にスクロール
    if (targetRef.current && scrollRef.current) {
      targetRef.current.scrollIntoView({ block: 'center' })
    }
  }, [])

  return (
    <>
      <h3 className="text-sm font-medium text-muted-foreground">回答にするセグメントを選択</h3>
      <div ref={scrollRef} className="max-h-60 overflow-y-auto space-y-1">
        {segments.map((seg, i) => {
          if (i === segmentIndex || seg.text === SILENCE_TEXT) return null
          // segmentIndex の直前のセグメントに ref を付けてスクロール基準にする
          const isAnchor = i === segmentIndex - 1 || (segmentIndex === 0 && i === segmentIndex + 1)
          return (
            <button
              key={i}
              ref={isAnchor ? targetRef : undefined}
              onClick={() => onSelect(i)}
              className={`w-full p-2 rounded text-left text-sm transition-colors ${
                selectedBackSegmentIndex === i
                  ? 'bg-primary/10 border border-primary'
                  : 'hover:bg-accent border border-transparent'
              }`}
            >
              <span className="text-xs text-muted-foreground mr-2">#{i}</span>
              {seg.text}
            </button>
          )
        })}
      </div>
    </>
  )
}
